@if(error) {
  <div class="alert alert-danger">{{error}}</div>
}

<h1 data-automation-name="metadataReportIndex">
  @if(editing) {
    <span>Edit Metadata Report {{metadataReport.id}}</span>
  } @else {
    <span>Register a new Metadata Report</span>
  }
</h1>

@if(editing) {
  <div class="alert alert-info">
    Modifying the report specifier id or changing which RIDs are attached to this report will cause any existing registered report to be cancelled and this metadata report to be re-registered.
  </div>
}

<form name="metadataReportForm" class="form-horizontal" [formGroup]="metadataReportForm">
  <div class="form-group">
    <label for="metadataReportId"> Report specifier id: </label>
    <input id="metadataReportId" class="form-control" name="id" type="text" [formControl]="metadataReportForm.controls.report_specifier_id" required>
  </div>

  <div class="form-group">
    <label for="metadataReportLabel">(Optional) Label: </label>
    <input id="metadataReportLabel" class="form-control" name="label" type="text" [formControl]="metadataReportForm.controls.label">
  </div>

  <div class="form-group">
    <label for="metadataReportDataSource">Data source: </label>
    <select id="metadataReportDataSource" class="form-control" name="data_source" type="text" [formControl]="metadataReportForm.controls.dataSource" required>
      <option value="x-energy">X-Energy</option>
      <option value="basic">BASIC</option>
      <option value="static">Static</option>
      <option value="eps">EPS</option>
    </select>
  </div>

  <div class="form-group">
    <label for="metadataReportType">Report type: </label>
    <select id="metadataReportType" class="form-control" name="report_type" type="text" [formControl]="metadataReportForm.controls.reportType" required>
      <option value="TELEMETRY_USAGE">Telemetry usage</option>
      <option value="TELEMETRY_STATUS">Telemetry status</option>
    </select>
  </div>

  @if(metadataReportForm.get('dataSource')?.value === 'x-energy' || metadataReportForm.get('dataSource')?.value === 'static') {
    <div class="form-group">
      <label for="metadataReportStyle">Report style: </label>
      <select id="metadataReportStyle" class="form-control" name="report_style" type="text" [formControl]="metadataReportForm.controls.reportStyle">
        <option value="INTERVAL_START">Interval Start</option>
        <option value="INTERVAL_END">Interval End</option>
      </select>
    </div>
  }

  <div class="row">
    <div class="col-md-6">
      <label for="included_rids">Included RIDs</label>
      <table id="included_rids" class="table table-striped">
        <thead>
        <tr>
          <th colspan="2">
            <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="searchTextGroupSelected" name="search" [ngModelOptions]="{standalone: true}"/>
          </th>
        </tr>
        <tr>
          <th></th>
          <th>ID</th>
        </tr>
        </thead>
        <tbody>
          @for(rid of displayedIncludedRids | searchFilterPipe : searchTextGroupSelected : 'id' | paginate: ridsSelectedPagination; track rid) {
            <tr id="rid_included_{{rid.id}}">
              <td><button id="remove" class="btn btn-primary" type="button" (click)="removeRid(rid)">Remove</button></td>
              <td>{{rid.id}}</td>
            </tr>
          }
          @if(metadataReport.rids.length === 0) {
            <tr>
              <td colspan="2" style="text-align: center;"> No RID on report.</td>
            </tr>
          }
        </tbody>
        <tfoot>
        <tr><td class="text-center" colspan="2"><pagination-controls id="ridsSelectedPagination" (pageChange)="handlePageChange($event, ridsSelectedPagination)" class="my-pagination"></pagination-controls></td></tr>
        </tfoot>
      </table>
    </div>

    <div class="col-md-6">
      <label for="possible_rids">Possible RIDs</label>
      <table id="possible_rids" class="table table-striped">
        <thead>
        <tr>
          <th colspan="2">
            <input placeholder="Search" class="input-sm form-control" type="search" [(ngModel)]="possibleSearchTextGroupSelected" name="search" [ngModelOptions]="{standalone: true}"/>
          </th>
        </tr>
        <tr>
          <th></th>
          <th>ID</th>
        </tr>
        </thead>
        <tbody>
          @for(rid of displayedPossibleRids | searchFilterPipe : possibleSearchTextGroupSelected : 'id' | paginate: possibleRidsSelectedPagination; track rid) {
            <tr id="rid_possible_{{rid.id}}">
              <td><button id="include" class="btn btn-primary" type="button" (click)="includeRid(rid)">Include</button></td>
              <td>{{rid.id}}</td>
            </tr>
          }
          @if(displayedPossibleRids.length <= 0) {
            <tr>
              <td colspan="2" style="text-align: center;"> No RIDs exist yet.</td>
            </tr>
          }
        </tbody>
        <tfoot>
        <tr><td class="text-center" colspan="2"><pagination-controls id="possibleRidsPagination" (pageChange)="handlePageChange($event, possibleRidsSelectedPagination)" class="my-pagination"></pagination-controls></td></tr>
        </tfoot>
      </table>
    </div>
  </div>

  <div class="form-group">
    <label for="report_offset"> (Optional) Report offset (must be valid ISO duration): </label>
    <br/>
    <small>If this field is set, all registered reports created from this metadata report will be delayed by this duration.</small>
    <br/>
    <small>For example, a report scheduled for 10:00 - 10:30 with an offset of PT5M will be sent from 10:05 - 10:35.</small>
    <br/>
    <small><strong>Use PT0S, PT0M, or PT0H to specify zero offset or leave it blank.</strong></small>
    <br/>
    <input id="report_offset" placeholder="PT3H, PT0S, PT30M" class="form-control" type="text" [formControl]="metadataReportForm.controls.reportOffset"
           [ngClass]="{'has-error': metadataReport.report_offset && !isValidIsoDuration(metadataReport.report_offset)}">
    @if(metadataReport.report_offset && !isValidIsoDuration(metadataReport.report_offset)) {
      <div class="text-danger">
        <small>Invalid ISO duration format. Use formats like PT3H, PT30M, PT0S, etc.</small>
      </div>
    }
    @if(metadataReport.report_offset && isZeroDuration(metadataReport.report_offset)) {
      <div class="text-info">
        <small>Zero offset specified - reports will be sent immediately at scheduled time.</small>
      </div>
    }
  </div>

  <div class="form-group">
    <label for="backfill_label"> (Optional) Backfill Label: </label>
    <br/>
    <small>The backfill label will be added to the end of Report IDs when sending backfill reports.</small>
    <br/>
    <small> Required by some VTNs, like Enbala.</small>
    <br/>
    <input id="backfill_label" placeholder="-BACKFILL" class="form-control" type="text" [formControl]="metadataReportForm.controls.backfillLabel">
  </div>

  <div class="form-group">
    <label for="include_report_rid_prefix" class="form-check-label">(Optional) Include report RID prefix</label>
    <input id="include_report_rid_prefix" class="form-check-input" style="margin-left: 5px" type="checkbox" [formControl]="metadataReportForm.controls.includeReportRidPrefix">
  </div>

  <div class="form-group">
    <label for="send_updates" class="form-check-label">(Optional) Send updates?</label>
    <input id="send_updates" class="form-check-input" style="margin-left: 5px" type="checkbox" [formControl]="metadataReportForm.controls.sendUpdates">
  </div>

  @if(metadataReport.send_updates) {
    <div class="form-group">
      <label for="update_type"> Update Type: </label>
      <select id="update_type" class="form-control" name="update_type" type="select" [formControl]="metadataReportForm.controls.updateType">
        <option value="scheduled">Scheduled</option>
        <option value="window">Window</option>
      </select>
    </div>
  }

  @if(metadataReport.update_type === "scheduled") {
    <div class="form-group">
      <label for="scheduled_update_time"> Time of day to send: </label>
      <input id="scheduled_update_time" placeholder="04:00:00" class="form-control" type="text" [formControl]="metadataReportForm.controls.scheduledUpdateTime" required>
    </div>
  }

  @if(metadataReport.update_type === "window") {
    <div class="form-group">
      <label for="scheduled_update_duration"> Window duration length: </label>
      <input id="scheduled_update_duration" placeholder="PT3H" class="form-control" type="text" [formControl]="metadataReportForm.controls.scheduledUpdateDuration" required>
    </div>
  }

  <div class="row">
    <button id="save" class="btn btn-primary" type="button" (click)="save()">Save</button>
    <a class="btn" (click)="backPage('/metadataReports', router)">Cancel</a>
  </div>
</form>
